import { isPermissionDeniedError } from './authSession'

export const PERMISSION_DENIED_EVENT = 'app-permission-denied'

/**
 * Fired when a backend API call returns MODULE_SUBSCRIPTION_EXPIRED (403).
 * Detail shape: { module: "nerve" | "bruno" | "bridge", message: string }
 *
 * DashboardLayout listens for this event and immediately:
 *   1. Removes the expired module from sessionModules state.
 *   2. Re-renders the sidebar/topbar (hides expired module menus).
 *   3. Shows the "Subscription Required" overlay if the user is currently
 *      on a page that belongs to the expired module.
 */
export const SUBSCRIPTION_EXPIRED_EVENT = 'app-subscription-expired'

/**
 * Fired when a subscription or trial is successfully activated or renewed.
 * Detail shape: { modules: string[] }
 *
 * useAuth and DashboardLayout listen for this event and immediately:
 *   1. Updates sessionModules state in real-time without requiring re-login.
 *   2. Updates localStorage user.modules so page refreshes retain the module.
 *   3. Re-renders navigation and clears any subscription-required blocks.
 */
export const SUBSCRIPTION_ACTIVATED_EVENT = 'app-subscription-activated'

export function emitSubscriptionActivated(newModules) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(SUBSCRIPTION_ACTIVATED_EVENT, { detail: { modules: newModules } })
  )
}

function classifyAction(method) {
  const normalized = String(method || '').toUpperCase()
  if (normalized === 'GET') return 'view'
  if (normalized === 'POST') return 'create'
  if (normalized === 'PUT' || normalized === 'PATCH') return 'edit'
  return null
}

function shouldSuppressPermissionModal(urlPath) {
  const value = String(urlPath || '').toLowerCase()
  if (!value) return false
  return (
    value.includes('/auth/me')
    || value.includes('/options')
    || value.includes('/chat/unread-summary')
    || value.includes('/notifications/summary')
    || value.includes('/activity-monitor/logout')
  )
}

function deriveRouteLabel(pathname) {
  const segments = String(pathname || '').split('/').filter(Boolean)
  if (!segments.length) return 'Current Module'
  const first = segments[0]
  const second = segments[1] || ''
  const toTitle = (value) => String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
  if (!second) return toTitle(first)
  return `${toTitle(first)} / ${toTitle(second)}`
}

export function emitPermissionDeniedEvent(detail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail }))
}

/**
 * Checks if the Axios error is a MODULE_SUBSCRIPTION_EXPIRED response.
 * Returns the detail object `{ module, message }` or null.
 */
function extractSubscriptionExpiredDetail(error) {
  const status = Number(error?.response?.status || 0)
  if (status !== 403) return null
  const detail = error?.response?.data?.detail
  if (typeof detail !== 'object' || detail?.code !== 'MODULE_SUBSCRIPTION_EXPIRED') return null
  return {
    module: String(detail?.module || '').toLowerCase(),
    message: String(detail?.message || 'Subscription expired.'),
  }
}

export function maybeEmitPermissionDeniedFromApiError(error) {
  // ── Priority 1: Module subscription expiry ──────────────────────────────
  // Must be checked BEFORE the generic permission-denied path so we emit the
  // correct, more specific event (SUBSCRIPTION_EXPIRED_EVENT) and skip the
  // generic AccessDeniedModal that PERMISSION_DENIED_EVENT triggers.
  const subscriptionDetail = extractSubscriptionExpiredDetail(error)
  if (subscriptionDetail) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT, { detail: subscriptionDetail }),
      )
    }
    // Do NOT fall through — we've handled this error.
    return
  }

  // ── Priority 2: Standard RBAC 403 ───────────────────────────────────────
  if (!isPermissionDeniedError(error)) return
  const method = String(error?.config?.method || '').toUpperCase()
  const urlPath = String(error?.config?.url || '')
  if (shouldSuppressPermissionModal(urlPath)) return
  const action = classifyAction(method)
  if (!action) return

  const routeLabel = deriveRouteLabel(window?.location?.pathname || urlPath)
  emitPermissionDeniedEvent({
    action,
    routeLabel,
    source: `api:${method || 'UNKNOWN'}`,
  })
}

